import { Fragment } from 'react/jsx-runtime';
import { VideoIcon } from '@room-match/shared-ui-components';
import { FileType } from 'shared/enums/FileType';
import { MediaFileType } from 'shared/enums/MediaFileType';
import { useEditProfileMediaDocumentData } from '../../hooks/useEditProfileMediaDocumentData';
import { array_skeletons } from 'pages/Representatives/const/array_skeleton';
import { EditProfileCustomSkeleton } from '../../EditProfile.styles';
import { useEditProfileMediaHideClick } from '../../hooks/useEditProfileMediaHideClick';
import { AgencyPermissionEnums } from 'shared/enums/AgencyPermissionEnums';
import { EttachedHiddenType } from 'shared/enums/EAttachedHiddenType';
import { EditProfileDocumentsVideoListContainer } from './EditProfileMediaDocumentsVideo.styles';
import { useEditProfileUploadingFiles } from '../../hooks/useEditProfileUploadingFiles';
import { EditProfileDocumentsVideoItemWrapper } from './EditProfileMediaDocumentVideoItem/EditProfileMediaDocumentVideoItem.styles';
import { IRepresentativeSingleProfileMedia } from 'shared/interfaces/IRepresentativesEditProfile';
import { useCallback, useMemo } from 'react';
import EditProfileMediaDocumentAccordion from '../EditProfileMediaDocumentAccordion/EditProfileMediaDocumentAccordion';
import EditProfileMediaDocumentDivider from '../EditProfileMediaDocumentDivider/EditProfileMediaDocumentDivider';
import RepresentativeErrorUI from 'pages/Representatives/components/RepresentativeErrorUI/RepresentativeErrorUI';
import EditProfileMediaDnD from '../EditProfileMediaDnD/EditProfileMediaDnD';
import EditProfileMediaDocumentVideoItem from './EditProfileMediaDocumentVideoItem/EditProfileMediaDocumentVideoItem';
import EditProfileNoMediaText from '../EditProfileNoMediaText/EditProfileNoMediaText';
import EditProfileMediaUploadingItem from '../EditProfleMediaUploadingItem/EditProfileMediaUploadingItem';

const EditProfileMediaDocumentsVideo = () => {
  const {
    attached_media: attached_videos,
    hidden_media: hidden_videos,
    completeness,
    media_loading,
    media_error,
    handleRefetch,
    profileId,
  } = useEditProfileMediaDocumentData(FileType.VIDEO);
  const { onHideClick, unHideClick } = useEditProfileMediaHideClick(
    FileType.VIDEO,
    profileId,
    AgencyPermissionEnums.VIDEOS,
  );
  const { uploadedFiles, handleSetUploadedFiles, handleRemoveUploadedFiles } = useEditProfileUploadingFiles(
    FileType.VIDEO,
  );

  // Memoization for the Rendered Group in DND component
  const renderedGroupMemoized = useCallback((attached_data: IRepresentativeSingleProfileMedia[]) => {
    return attached_data?.map((attached_video, index) => (
      <EditProfileMediaDocumentVideoItem
        key={attached_video.id}
        url={
          attached_video?.medium?.attachment_url ||
          attached_video?.medium?.cropped_attachment_url ||
          attached_video?.medium?.modified_attachment_url ||
          ''
        }
        index={index + 1}
        type={EttachedHiddenType.ATTACHED}
        fileName={attached_video?.medium?.file_name || ''}
        onHideClick={() => {
          onHideClick(attached_video?.id);
        }}
        id={attached_video.id}
      />
    ));
  }, []);

  // Memoization for the Overlay in DND component
  const renderedOverlayMemoized = useCallback(
    (activeData: IRepresentativeSingleProfileMedia, activeId: number) => (
      <EditProfileMediaDocumentVideoItem
        key={activeData.id}
        url={
          activeData?.medium?.attachment_url ||
          activeData?.medium?.cropped_attachment_url ||
          activeData?.medium?.modified_attachment_url ||
          ''
        }
        index={activeId + 1}
        type={EttachedHiddenType.ATTACHED}
        fileName={activeData?.medium?.file_name || ''}
        id={activeData.id}
      />
    ),
    [],
  );

  // Memoized Hidden Media
  const hiddenMediaMemoized = useMemo(() => {
    return hidden_videos?.map((hidden_video) => (
      <EditProfileMediaDocumentVideoItem
        key={hidden_video.id}
        url={
          hidden_video.attachment_url ||
          hidden_video.cropped_attachment_url ||
          hidden_video.modified_attachment_url ||
          ''
        }
        type={EttachedHiddenType.HIDDEN}
        fileName={hidden_video.file_name || ''}
        onHideClick={() => {
          unHideClick({
            id: hidden_video.id,
            tag_list: MediaFileType.VIDEO,
          });
        }}
        id={hidden_video.id}
      />
    ));
  }, [hidden_videos]);

  return (
    <EditProfileMediaDocumentAccordion
      type={FileType.VIDEO}
      usedLength={attached_videos?.length || 0}
      maxLength={(hidden_videos?.length || 0) + (attached_videos?.length || 0)}
      header_icon={<VideoIcon />}
      completeness={completeness}
      handleSetUploadedFiles={handleSetUploadedFiles}
    >
      {media_loading ? (
        <EditProfileDocumentsVideoListContainer>
          {array_skeletons.map((_, index) => (
            <EditProfileDocumentsVideoItemWrapper key={index}>
              <EditProfileCustomSkeleton width="100%" height="100%" variant="rect" />
            </EditProfileDocumentsVideoItemWrapper>
          ))}
        </EditProfileDocumentsVideoListContainer>
      ) : media_error ? (
        <RepresentativeErrorUI message={'There was a problem getting your Videos'} onButtonClick={handleRefetch} />
      ) : (
        <Fragment>
          {attached_videos?.length > 0 || uploadedFiles?.length > 0 ? (
            <EditProfileDocumentsVideoListContainer>
              {/* Attached List */}
              <EditProfileMediaDnD
                data={attached_videos}
                profileId={profileId}
                agencyPermissionValue={AgencyPermissionEnums.VIDEOS}
                renderGroup={renderedGroupMemoized}
                renderDragOverlay={renderedOverlayMemoized}
              />
              {/* Uploading list */}
              {uploadedFiles?.length > 0
                ? uploadedFiles?.map((uploadedFile) => (
                    <EditProfileMediaUploadingItem
                      // Dont forget the key, this prevents the infinite loop, dont put index, better use UUID
                      key={uploadedFile.id}
                      file={uploadedFile.file}
                      profileId={profileId}
                      mediaFileType={MediaFileType.VIDEO}
                      onDone={() => handleRemoveUploadedFiles(uploadedFile.id)}
                    />
                  ))
                : null}
            </EditProfileDocumentsVideoListContainer>
          ) : (
            <EditProfileNoMediaText type={FileType.VIDEO} />
          )}

          {/* Hidden List */}
          {hidden_videos?.length === 0 ? null : (
            <EditProfileMediaDocumentDivider type={MediaFileType.VIDEO} key={'hidden'}>
              <EditProfileDocumentsVideoListContainer>{hiddenMediaMemoized}</EditProfileDocumentsVideoListContainer>
            </EditProfileMediaDocumentDivider>
          )}
        </Fragment>
      )}
    </EditProfileMediaDocumentAccordion>
  );
};

export default EditProfileMediaDocumentsVideo;
